import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, UserPlus, Ban, CircleCheck, UserX, Clock, Trash2 } from 'lucide-react';
import { User, Role } from '../../../../shared/types/user.types';
import useUser from '../../wrappers/use-user';
import useSettingLists from '../../wrappers/use-setting-list-item';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { accountsListStyles } from './AccountsList.styles';

const PAGE_SIZE = 100;

const AccountsList: React.FC = () => {
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const navigate = useNavigate();
    const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dealerMap, setDealerMap] = useState<Record<string, string>>({});
    const { list } = useUser();
    const settingLists = useSettingLists();

    useEffect(() => {
        settingLists.list('dealers').then((items) => {
            setDealerMap(Object.fromEntries(items.map((i) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const roleConfig = {
        admin: { label: t('role.admin'), color: 'secondary.main' },
        dealer: { label: t('role.dealer'), color: 'primary.main' },
        customer: { label: t('role.customer'), color: 'text.secondary' },
    };

    const getAuthState = (user: User) => {
        const created = (user.providers?.length ?? 0) > 0;
        if (!created) return { label: t('accountsList.authNotCreated'), color: 'text.secondary', icon: <UserX size={14} /> };
        if (user.disabled) return { label: t('accountStatus.disabled'), color: 'error.main', icon: <Ban size={14} /> };
        return { label: t('accountStatus.clean'), color: 'success.main', icon: <CircleCheck size={14} /> };
    };

    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Sledujeme předchozí hodnoty filtrů, abychom rozlišili změnu filtru od změny stránky.
    const prevFilters = useRef({ roleFilter, debouncedSearch });

    useEffect(() => {
        const filtersChanged =
            prevFilters.current.roleFilter !== roleFilter ||
            prevFilters.current.debouncedSearch !== debouncedSearch;
        prevFilters.current = { roleFilter, debouncedSearch };

        // Pokud se změnil filtr a jsme na stránce > 0, nejdřív resetujeme stránku na 0.
        // Efekt se pak spustí znovu s page=0 a teprve tehdy odešleme request.
        // Tím se vyhneme dvojitému requestu (jednou se starým offsetem, jednou s novým).
        if (filtersChanged && page !== 0) {
            setPage(0);
            return;
        }

        setLoading(true);
        const filter = {
            ...(roleFilter !== 'all' && { role: roleFilter }),
            ...(debouncedSearch && { search: debouncedSearch }),
        };
        list(filter, { limit: PAGE_SIZE, offset: page * PAGE_SIZE }).then(({ users: data, total: count }) => {
            setUsers(data);
            setTotal(count);
            setLoading(false);
        }).catch((err) => {
            console.error('Failed to fetch users:', err);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, roleFilter, debouncedSearch]);

    return (
        <Box sx={accountsListStyles.root}>
            <Box sx={accountsListStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('accountsList.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('accountsList.subtitle')}</Typography>
                </Box>
                <Button variant="contained" startIcon={<UserPlus size={20} />} component={Link} to="/app/admin/users/new">
                    {t('accountsList.newAccount')}
                </Button>
            </Box>

            <Paper elevation={0} sx={accountsListStyles.filtersPaper}>
                <TextField
                    size="small"
                    placeholder={t('accountsList.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search size={20} /></InputAdornment> }}
                    sx={accountsListStyles.searchField}
                />
                <ToggleButtonGroup value={roleFilter} exclusive onChange={(_, v) => v && setRoleFilter(v)} size="small">
                    <ToggleButton value="all" sx={accountsListStyles.roleToggle}>{t('accountsList.filterAll')}</ToggleButton>
                    <ToggleButton value="customer" sx={accountsListStyles.roleToggle}>{t('role.customer')}</ToggleButton>
                    <ToggleButton value="dealer" sx={accountsListStyles.roleToggle}>{t('role.dealer')}</ToggleButton>
                    <ToggleButton value="admin" sx={accountsListStyles.roleToggle}>{t('role.admin')}</ToggleButton>
                </ToggleButtonGroup>
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={accountsListStyles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow sx={accountsListStyles.tableHeaderRow}>
                            <TableCell sx={accountsListStyles.tableHeaderCell}>{t('accountsList.colUser')}</TableCell>
                            <TableCell sx={accountsListStyles.tableHeaderCell}>{t('accountsList.colRole')}</TableCell>
                            <TableCell sx={accountsListStyles.tableHeaderCell}>{t('accountsList.colStatus')}</TableCell>
                            <TableCell sx={accountsListStyles.tableHeaderCell}>{t('accountsList.colCreated')}</TableCell>
                            <TableCell sx={accountsListStyles.tableHeaderCell}>{t('accountsList.colLastLogin')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={accountsListStyles.loadingCell}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && users.map((user) => {
                            const role = roleConfig[user.role];
                            const authState = getAuthState(user);
                            return (
                                <TableRow key={user.uid} hover onClick={() => navigate(`/app/admin/users/${user.uid}`)} sx={{ cursor: 'pointer' }}>
                                    <TableCell>
                                        <Box sx={accountsListStyles.userCell}>
                                            <Avatar sx={accountsListStyles.userAvatar}>
                                                {user.name.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color={role.color} fontWeight={500}>{role.label}</Typography>
                                        {user.role === 'dealer' && user.dealerId && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {dealerMap[user.dealerId] ?? user.dealerId}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: authState.color }}>
                                                {authState.icon}
                                                <Typography variant="body2" color="inherit">{authState.label}</Typography>
                                            </Box>
                                            {user.status === 'pending' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'warning.main' }}>
                                                    <Clock size={14} />
                                                    <Typography variant="body2" color="inherit">{t('accountStatus.pending')}</Typography>
                                                </Box>
                                            )}
                                            {user.status === 'requestDelete' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'error.main' }}>
                                                    <Trash2 size={14} />
                                                    <Typography variant="body2" color="inherit">{t('accountStatus.requestDelete')}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(user.lastSignInTime)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && users.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={accountsListStyles.emptyCell}>
                                    {t('accountsList.noUsers')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    labelDisplayedRows={({ from, to, count }) => t('accountsList.pagination', { from, to, count })}
                />
            </TableContainer>
        </Box>
    );
};

export default AccountsList;
